import { LoginRequest, LoginResponse } from "@/types";
import { axiosInstance } from "@/lib/axios";

/**
 * Login with email and password
 */
export const login = async (
  credentials: LoginRequest
): Promise<LoginResponse> => {
  const response = await axiosInstance.post<LoginResponse>(
    `/auth/admin/login`,
    credentials
  );
  return response.data;
};

/**
 * Request a forgot-password reset email for an admin.
 * Backend always returns the same message whether or not an account exists
 * (account-enumeration prevention).
 */
export const forgotPassword = async (
  email: string
): Promise<{ message: string }> => {
  const response = await axiosInstance.post<{ message: string }>(
    `/auth/admin/forgot-password`,
    { email }
  );
  return response.data;
};

/**
 * Reset an admin password using the token received via email from the
 * forgot-password flow. Distinct from `adminSignup`, which uses an invitation
 * token to create the Stytch account for the first time.
 */
export const resetPassword = async (data: {
  token: string;
  password: string;
}): Promise<{ message: string }> => {
  const response = await axiosInstance.post<{ message: string }>(
    `/auth/admin/reset-password`,
    data
  );
  return response.data;
};

/**
 * Logout - clears auth tokens
 */
export const logout = (): void => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("sessionJwt");
    localStorage.removeItem("sessionToken");
  }
};

/**
 * Get stored session JWT
 */
export const getSessionJwt = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("sessionJwt");
  }
  return null;
};

/**
 * Get stored session token
 */
export const getSessionToken = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("sessionToken");
  }
  return null;
};

/**
 * Store session tokens
 */
export const storeSessionTokens = (
  sessionJwt: string,
  sessionToken: string
): void => {
  if (typeof window !== "undefined") {
    localStorage.setItem("sessionJwt", sessionJwt);
    localStorage.setItem("sessionToken", sessionToken);
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getSessionJwt();
};

